import React from "react";
import {
  Grid,
  Typography,
  Stack,
  Avatar,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import moment from "moment";

const RequestHistory = ({ selectedData, setOpenHistoryDrawer }) => {
  const formatChanges = (text) => {
    if (!text) return text;

    return text
      .replace(
        /(Created By|for|Status|changed|from|to|by|")/gi,
        "<strong>$&</strong>"
      )
      .replace(/\b([A-Z][a-z]+ [A-Z][a-z]+)\b/g, "<strong>$&</strong>");
  };

  const getUserNameFromChanges = (changes) => {
    const createdByMatch = changes.match(/Created By (.*?)( for|$)/);
    if (createdByMatch && createdByMatch[1]) {
      return createdByMatch[1].trim();
    }
    const changedByMatch = changes.match(/by (.*?)\./);
    if (changedByMatch && changedByMatch[1]) {
      return changedByMatch[1].trim();
    }
    return "Unknown";
  };

  const getStatusChange = (changes) => {
    const statusMatch = changes.match(/Status changed from (.*?) to (.*?)( by|\.|$)/);
    if (statusMatch) {
      return { from: statusMatch[1], to: statusMatch[2] };
    }
    return null;
  };

  const sortedHistory = selectedData?.history
    ?.slice()
    ?.sort((a, b) => new Date(b.date) - new Date(a.date))
    ?.filter(task => {
      const taskDateString = task?.date;
      return taskDateString && !isNaN(new Date(taskDateString).getTime());
    });

  return (
    <>
      <DialogTitle sx={{ py: 2, px: 3 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          borderBottom=  "1px solid"
          borderColor="divider"
          pb={1}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <HistoryIcon color="primary" />
            <Typography variant="h6" fontWeight="600">
              Request History
            </Typography>
            <Chip 
              label={`${sortedHistory?.length || 0} entries`} 
              size="small" 
              variant="outlined"
            />
          </Stack>
          <IconButton onClick={() => setOpenHistoryDrawer(false)}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 3 }}>
          {!sortedHistory || sortedHistory.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <HistoryIcon sx={{ fontSize: 48, mb: 2, opacity: 0.3 }} />
              <Typography variant="body1" color="text.secondary">
                No history available
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              {sortedHistory.map((task, index) => {
                const userName = getUserNameFromChanges(task.changes);
                const statusChange = getStatusChange(task.changes);
                const isCreation = task.changes?.includes('Created By');

                return (
                  <Box
                    key={index}
                    sx={{
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      backgroundColor: 'background.paper'
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                      <Avatar
                        alt={userName}
                        src={task?.avatarUrl}
                        sx={{
                          backgroundColor: 'action.hover',
                          color: 'text.primary',
                          height: 40,
                          width: 40,
                          fontSize: "14px"
                        }}
                      >
                        {userName?.charAt(0).toUpperCase()}
                      </Avatar>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack 
                          direction="row" 
                          justifyContent="space-between" 
                          alignItems="flex-start"
                          sx={{ mb: 1 }}
                        >
                          <Box>
                            <Typography variant="subtitle2" fontWeight="600">
                              {userName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {moment.utc(task.date).format("MMM Do YYYY, h:mm a")}
                            </Typography>
                          </Box>
                          {isCreation && (
                            <Chip
                              label="Created"
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          )}
                        </Stack>

                        {statusChange && (
                          <Box sx={{ mb: 1.5 }}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Typography variant="caption" color="text.secondary">
                                Status:
                              </Typography>
                              <Chip
                                label={statusChange.from}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '11px', height: 20 }}
                              />
                              <Typography variant="caption" color="text.secondary">
                                →
                              </Typography>
                              <Chip
                                label={statusChange.to}
                                size="small"
                                color="primary"
                                sx={{ fontSize: '11px', height: 20 }}
                              />
                            </Stack>
                          </Box>
                        )}

                        <Box
                          sx={{
                            color: 'text.primary',
                            fontSize: "14px",
                            lineHeight: 1.5,
                            '& strong': {
                              fontWeight: 600
                            }
                          }}
                          dangerouslySetInnerHTML={{
                            __html: formatChanges(task.changes),
                          }}
                        />
                      </Box>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Box>
      </DialogContent>
    </>
  );
};

export default RequestHistory;